import { getIncidents, updateIncident, INCIDENT_STATUS, INCIDENT_PRIORITY } from './incidentsService';

// Функція для отримання перекладів (буде використовувати глобальний контекст i18n)
const getTranslation = (key, lang = 'en') => {
  const translations = {
    en: {
      'escalation_critical_message': 'Critical incident requires immediate attention',
      'escalation_time_message': 'Incident escalated due to prolonged resolution time',
      'escalation_response_message': 'Priority increased due to lack of responses',
      'critical_escalation': 'Critical Escalation',
      'time_escalation': 'Time Escalation',
      'response_time_escalation': 'Response Time Escalation',
      'notification_critical_title': '🔄 Automatic Critical Incident Escalation',
      'notification_critical_message': 'Critical incident "{type}" automatically moved to in progress',
      'notification_time_title': '⏰ Escalation Due to Long Time',
      'notification_time_message': 'Incident priority increased due to long resolution time',
      'notification_response_title': '📨 Escalation Due to No Responses',
      'notification_response_message': 'Incident priority increased due to lack of responses'
    },
    uk: {
      'escalation_critical_message': 'Критичний інцидент потребує негайної уваги',
      'escalation_time_message': 'Інцидент ескальовано через тривалий час вирішення',
      'escalation_response_message': 'Пріоритет підвищено через відсутність відповідей',
      'critical_escalation': 'Критична Ескалація',
      'time_escalation': 'Ескалація за Часом',
      'response_time_escalation': 'Ескалація через Час Відповіді',
      'notification_critical_title': '🔄 Автоматична ескалація критичного інциденту',
      'notification_critical_message': 'Критичний інцидент "{type}" автоматично переведено в роботу',
      'notification_time_title': '⏰ Ескалація через тривалий час',
      'notification_time_message': 'Пріоритет інциденту підвищено через тривалий час вирішення',
      'notification_response_title': '📨 Ескалація через відсутність відповідей',
      'notification_response_message': 'Пріоритет інциденту підвищено через відсутність відповідей'
    },
    es: {
      'escalation_critical_message': 'Incidente crítico requiere atención inmediata',
      'escalation_time_message': 'Incidente escalado por tiempo prolongado de resolución',
      'escalation_response_message': 'Prioridad aumentada por falta de respuestas',
      'critical_escalation': 'Escalación Crítica',
      'time_escalation': 'Escalación por Tiempo',
      'response_time_escalation': 'Escalación por Tiempo de Respuesta',
      'notification_critical_title': '🔄 Escalación Automática de Incidente Crítico',
      'notification_critical_message': 'Incidente crítico "{type}" movido automáticamente a en progreso',
      'notification_time_title': '⏰ Escalación por Tiempo Prolongado',
      'notification_time_message': 'Prioridad del incidente aumentada por tiempo prolongado de resolución',
      'notification_response_title': '📨 Escalación por Falta de Respuestas',
      'notification_response_message': 'Prioridad del incidente aumentada por falta de respuestas'
    },
    ca: {
      'escalation_critical_message': 'Incidència crítica requereix atenció immediata',
      'escalation_time_message': 'Incidència escalada per temps prolongat de resolució',
      'escalation_response_message': 'Prioritat augmentada per falta de respostes',
      'critical_escalation': 'Escalació Crítica',
      'time_escalation': 'Escalació per Temps',
      'response_time_escalation': 'Escalació per Temps de Resposta',
      'notification_critical_title': '🔄 Escalació Automàtica d\'Incidència Crítica',
      'notification_critical_message': 'Incidència crítica "{type}" moguda automàticament a en progrés',
      'notification_time_title': '⏰ Escalació per Temps Prolongat',
      'notification_time_message': 'Prioritat de la incidència augmentada per temps prolongat de resolució',
      'notification_response_title': '📨 Escalació per Falta de Respostes',
      'notification_response_message': 'Prioritat de la incidència augmentada per falta de respostes'
    }
  };

  const langTranslations = translations[lang] || translations['en'];
  return langTranslations[key] || key;
};

// Функція для отримання поточної мови (можна покращити пізніше)
const getCurrentLanguage = () => {
  // Спроба отримати мову з localStorage або браузера
  try {
    const savedLang = localStorage.getItem('lainsa.language');
    if (savedLang) return savedLang;
    
    const browserLang = navigator.language.split('-')[0];
    if (['en', 'uk', 'es', 'ca'].includes(browserLang)) return browserLang;
  } catch (error) {
    console.error('Error getting language:', error);
  }
  
  return 'en'; // fallback
};

// Допоміжна функція для ескалації за часом
const checkTimeBasedEscalation = async (incident, now) => {
  const lang = getCurrentLanguage();
  const createdAt = new Date(incident.createdAt);
  const hoursSinceCreation = (now - createdAt) / (1000 * 60 * 60);
  
  // Якщо інцидент в роботі більше 24 годин - ескалація
  if (hoursSinceCreation > 24 && incident.status === INCIDENT_STATUS.IN_PROGRESS) {
    const updatedIncident = await updateIncident(incident.id, {
      priority: getEscalatedPriority(incident.priority),
      escalatedAt: new Date().toISOString(),
      autoEscalated: true
    });
    
    await sendEscalationNotification(incident, 'time_based_escalation', lang);
    
    return {
      incidentId: incident.id,
      type: 'time_escalation',
      message: getTranslation('escalation_time_message', lang),
      previousPriority: incident.priority,
      newPriority: getEscalatedPriority(incident.priority)
    };
  }
  
  return null;
};

// Допоміжна функція для ескалації за відсутністю відповідей
const checkResponseTimeEscalation = async (incident, now) => {
  const lang = getCurrentLanguage();
  
  if (!incident.responses || incident.responses.length === 0) {
    const createdAt = new Date(incident.createdAt);
    const hoursSinceCreation = (now - createdAt) / (1000 * 60 * 60);
    
    // Якщо немає відповідей більше 8 годин - ескалація
    if (hoursSinceCreation > 8) {
      const newPriority = getEscalatedPriority(incident.priority);
      const updatedIncident = await updateIncident(incident.id, {
        priority: newPriority,
        escalatedAt: new Date().toISOString(),
        autoEscalated: true
      });
      
      await sendEscalationNotification(incident, 'response_time_escalation', lang);
      
      return {
        incidentId: incident.id,
        type: 'response_time_escalation',
        message: getTranslation('escalation_response_message', lang),
        previousPriority: incident.priority,
        newPriority: newPriority
      };
    }
  }
  
  return null;
};

// Допоміжна функція для отримання підвищеного пріоритету
const getEscalatedPriority = (currentPriority) => {
  const priorityHierarchy = {
    [INCIDENT_PRIORITY.LOW]: INCIDENT_PRIORITY.MEDIUM,
    [INCIDENT_PRIORITY.MEDIUM]: INCIDENT_PRIORITY.HIGH,
    [INCIDENT_PRIORITY.HIGH]: INCIDENT_PRIORITY.CRITICAL,
    [INCIDENT_PRIORITY.CRITICAL]: INCIDENT_PRIORITY.CRITICAL
  };
  
  return priorityHierarchy[currentPriority] || INCIDENT_PRIORITY.MEDIUM;
};

// Допоміжна функція для відправки сповіщень про ескалацію
const sendEscalationNotification = async (incident, escalationType, lang = 'en') => {
  const messages = {
    critical_auto_escalation: {
      title: getTranslation('notification_critical_title', lang),
      message: getTranslation('notification_critical_message', lang).replace('{type}', incident.type)
    },
    time_based_escalation: {
      title: getTranslation('notification_time_title', lang),
      message: getTranslation('notification_time_message', lang)
    },
    response_time_escalation: {
      title: getTranslation('notification_response_title', lang),
      message: getTranslation('notification_response_message', lang)
    }
  };
  
  const notification = messages[escalationType];
  if (notification && window.notificationActions) {
    window.notificationActions.addNotification({
      type: 'escalation',
      priority: 'high',
      title: notification.title,
      message: notification.message,
      data: incident,
      category: 'incidents_escalation'
    });
  }
};

export const checkForEscalation = async () => {
  const incidents = await getIncidents();
  const now = new Date();
  const escalationResults = [];
  
  for (const incident of incidents) {
    // Ескалація за пріоритетом
    if (incident.priority === INCIDENT_PRIORITY.CRITICAL && incident.status === INCIDENT_STATUS.OPEN) {
      const escalationResult = await escalateCriticalIncident(incident);
      if (escalationResult) {
        escalationResults.push(escalationResult);
      }
    }
    
    // Ескалація за часом
    if (incident.status === INCIDENT_STATUS.IN_PROGRESS) {
      const timeEscalationResult = await checkTimeBasedEscalation(incident, now);
      if (timeEscalationResult) {
        escalationResults.push(timeEscalationResult);
      }
    }
    
    // Ескалація за відсутністю відповідей
    const responseEscalationResult = await checkResponseTimeEscalation(incident, now);
    if (responseEscalationResult) {
      escalationResults.push(responseEscalationResult);
    }
  }
  
  return escalationResults;
};

export const escalateCriticalIncident = async (incident) => {
  const lang = getCurrentLanguage();
  
  // Для критичних інцидентів автоматично змінюємо статус
  if (incident.status === INCIDENT_STATUS.OPEN) {
    const updatedIncident = await updateIncident(incident.id, {
      status: INCIDENT_STATUS.IN_PROGRESS,
      escalatedAt: new Date().toISOString(),
      autoEscalated: true
    });
    
    // Відправляємо сповіщення про ескалацію
    await sendEscalationNotification(incident, 'critical_auto_escalation', lang);
    
    return {
      incidentId: incident.id,
      type: 'critical_escalation',
      message: getTranslation('escalation_critical_message', lang),
      previousStatus: incident.status,
      newStatus: INCIDENT_STATUS.IN_PROGRESS
    };
  }
  
  return null;
};

export { getEscalatedPriority, sendEscalationNotification };